import * as React from 'react'
import Head from 'next/head'
import { useIntersectionObserver } from 'react-intersection-observer-hook'
import { useRouter } from 'next/router'

import { useAPIGetFilters } from 'api/filters/useAPIFilters'
import { useAPISearchAssessmentPacket } from 'api/assessment/useAPIAssessment'
import AssessmentCard from 'app/AssessmentPage/components/AssessmentCard'
import { SelectDiv } from 'components/SelectDiv'
import SheetSelectPhase from '../components/SheetSelectPhase'
import SheetSelectSubject from '../components/SheetSelectSubject'
import { EmptyState, EmptyStateAction } from '../components/EmptyState'
import { OfflineNotice } from 'components/OfflineNotice'
import { useOnlineStatus } from 'utils/hooks/useOnlineStatus'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { REFERRER_KEY } from 'utils/hooks/useRouterHistory'
import { USER_ID_STORAGE_KEY } from 'configs/auth'
import { GATrackEvent } from 'utils/googleAnalytics'
import { useFiltersStore } from 'stores/filters'
import useSatuEventAssessment from '../utils/useSatuEventAssessment'
import {
  EVENT_NAME_ASSESSMENT_LITNUM_VIEW_HOME_V2,
  EVENT_NAME_ASSESSMENT_LITNUM_CLICK_CARD_V1,
} from '../data'

import type { PacketFilterKeys } from 'types/assessment'

const getListingPageEventTriggerValue = (referrer: string) => {
  if (referrer?.includes('assessment/me')) {
    return 'SAVED_ASSESSMENTS'
  }
  if (referrer?.includes('assessment/litnum')) {
    return 'ASSESSMENT_LISTING'
  }
  return 'HOMEPAGE'
}

const AssessmentLists = () => {
  const { SETrackEventAssessment } = useSatuEventAssessment()
  const [, , , getReferrerValue] = useLocalStorage(REFERRER_KEY, null)
  const [userId] = useLocalStorage(USER_ID_STORAGE_KEY, null)
  const isOnline = useOnlineStatus()
  const [isPhaseOpen, setPhaseOpen] = React.useState(false)
  const [isSubjectOpen, setSubjectOpen] = React.useState(false)
  const [filter, setFilter] = React.useState({
    phase: '',
    subject: '',
  })
  const [ref, { entry }] = useIntersectionObserver()
  const router = useRouter()
  const loadingStateData = new Array(3).fill('')

  const {
    data: packets,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetched,
    refetch,
  } = useAPISearchAssessmentPacket({
    phase: filter.phase,
    subject: filter.subject,
    limit: 10,
  })

  React.useEffect(() => {
    if (entry && entry.isIntersecting) {
      fetchNextPage()
    }
  }, [entry, fetchNextPage])

  const filterLists = useFiltersStore((state) => state.filters)

  const {
    isError: areFiltersError,
    isLoading: areFiltersLoading,
    refetch: refetchFilters,
  } = useAPIGetFilters()

  React.useEffect(() => {
    const phase = (router.query.phase as string) ?? filterLists?.phases[0]
    const subject = (router.query.subject as string) ?? filterLists?.subjects[0]

    setFilter({
      phase,
      subject,
    })
    if (userId) {
      const referrer = getReferrerValue()
      const event_trigger = getListingPageEventTriggerValue(referrer)
      GATrackEvent({
        eventName: 'assessment_listing_page',
        eventParams: {
          event_trigger,
          user_id: userId,
          phase_id: phase,
          domain_id: subject,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLists, router])

  const handleFilterChange = (value: string, key: PacketFilterKeys) => {
    const newFilter = {
      ...filter,
      [key]: value,
    }
    setFilter(newFilter)

    if (userId) {
      const { phase: phase_id, subject: domain_id } = filter
      GATrackEvent({
        eventName:
          key === 'phase' ? 'click_filter_phase' : 'click_filter_domain',
        eventParams: {
          event_trigger: 'ASSESSMENT_LISTING',
          user_id: userId,
          ...(key === 'phase' ? { phase_id } : { domain_id }),
        },
      })
    }

    router.push({
      query: {
        ...newFilter,
      },
    })
  }

  const trackAssessmentClicked = ({
    index,
    id,
  }: {
    index: number
    id: string
  }) => {
    const position = index + 1
    const { phase: phase_id, subject: domain_id } = filter
    SETrackEventAssessment({
      eventName: EVENT_NAME_ASSESSMENT_LITNUM_CLICK_CARD_V1,
      eventParams: {
        event_detail: {
          assessment_id: id,
          assessment_phase: phase_id,
          assessment_subject: domain_id,
        },
      },
    })
    if (userId) {
      GATrackEvent({
        eventName: 'click_assessment_details',
        eventParams: {
          event_trigger: 'ASSESSMENT_LISTING',
          user_id: userId,
          assessment_id: id,
          item_position: position,
          phase_id,
          domain_id,
        },
      })
    }
  }

  React.useEffect(() => {
    SETrackEventAssessment({
      eventName: EVENT_NAME_ASSESSMENT_LITNUM_VIEW_HOME_V2,
      sendAttributes: false,
      sendUserAgent: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Head>
        <title>Asesmen Murid</title>
        <meta
          name="description"
          content="Paket soal untuk pahami kompetensi murid."
        />
      </Head>
      <SheetSelectPhase
        isOpen={isPhaseOpen}
        onClose={setPhaseOpen}
        selectedPhase={filter.phase}
        phases={filterLists?.phases || []}
        setValue={handleFilterChange}
      />
      <SheetSelectSubject
        isOpen={isSubjectOpen}
        onClose={setSubjectOpen}
        selectedSubject={filter.subject}
        subjects={filterLists?.subjects || []}
        setValue={handleFilterChange}
      />
      <div className="bg-[#006ed3] p-4 flex flex-col mt-14 overflow-x-hidden">
        <label htmlFor="phase" className="mb-1 text-sm text-white">
          Fase (Kelas)
        </label>
        <SelectDiv
          id="phase"
          onClick={() => setPhaseOpen(true)}
          title="phase"
          disabled={!isOnline || areFiltersError || areFiltersLoading}
        >
          <div role="textbox" title={filterLists?.phases[0]}>
            {filter.phase || filterLists?.phases[0]}
          </div>
        </SelectDiv>

        <label htmlFor="subjects" className="mt-5 mb-1 text-sm text-white">
          Mata pelajaran
        </label>
        <SelectDiv
          id="subjects"
          onClick={() => setSubjectOpen(true)}
          title="subject"
          disabled={!isOnline || areFiltersError || areFiltersLoading}
        >
          <div role="textbox" title={filterLists?.subjects[0]}>
            {filter.subject || filterLists?.subjects[0]}
          </div>
        </SelectDiv>
      </div>
      <div
        className="pt-4 px-4 pb-16 bg-[#f8f8f8] h-"
        style={{
          minHeight: 'calc(100vh - 17.5rem)',
        }}
      >
        {isOnline ? (
          <>
            {/* LOADING STATE */}
            {isLoading &&
              loadingStateData.map((_, idx) => (
                <AssessmentCard
                  key={idx}
                  numQuestions=""
                  phase=""
                  src=""
                  subjects=""
                  time=""
                  title=""
                  loading
                  className="mb-3"
                />
              ))}

            {/* FILTERS ERROR STATE */}
            {areFiltersError && (
              <EmptyState
                className="mt-8"
                title="Gagal Memuat Pilihan Fase dan Mata Pelajaran"
                subtitle="Maaf, ada masalah pada sistem kami. Mohon coba lagi."
              >
                <EmptyStateAction onClick={() => refetchFilters()}>
                  Coba Lagi
                </EmptyStateAction>
              </EmptyState>
            )}

            {/* PACKETS ERROR STATE */}
            {isError && (
              <EmptyState
                className="mt-8"
                title="Gagal Memuat Pilihan Asesmen"
                subtitle="Maaf, ada masalah pada sistem kami. Mohon coba lagi."
              >
                <EmptyStateAction onClick={() => refetch()}>
                  Coba Lagi
                </EmptyStateAction>
              </EmptyState>
            )}

            {isFetched && (
              <>
                {packets?.pages[0].data.length === 0 && (
                  <EmptyState
                    className="mt-16"
                    title="Asesmen Tidak Ditemukan"
                    subtitle="Cari asesmen dengan fase dan mata pelajaran yang berbeda."
                  />
                )}
                {packets?.pages.map((page) =>
                  page.data.map((packet, index) => (
                    <AssessmentCard
                      href={`/assessment/litnum/${packet.id}`}
                      loading={false}
                      key={packet.id}
                      numQuestions={packet.totalQuestionsQty}
                      phase={packet.phase}
                      src={packet.imageFileUrl}
                      subjects={packet.subject}
                      time={packet.duration}
                      title={packet.name}
                      className="mb-3"
                      onClick={() =>
                        trackAssessmentClicked({
                          index,
                          id: packet.id,
                        })
                      }
                    />
                  ))
                )}
                {hasNextPage
                  ? loadingStateData.map((_, idx) => (
                      <>
                        <AssessmentCard
                          key={idx}
                          numQuestions=""
                          phase=""
                          src=""
                          subjects=""
                          time=""
                          title=""
                          loading
                          className="mb-3"
                        />
                        <div ref={ref} />
                      </>
                    ))
                  : !(packets?.pages[0].data.length === 0) &&
                    !isError && (
                      <div className="flex items-center justify-center mx-4 text-sm text-gray-400">
                        Semua paket soal sudah ditampilkan.
                      </div>
                    )}
              </>
            )}
          </>
        ) : (
          <OfflineNotice className="absolute left-0 px-4 transform -translate-y-1/2 top-1/2" />
        )}
      </div>
    </>
  )
}

export default AssessmentLists
